import {getAuthToken} from "./authService";

export type Stage = "INT" | "QA" | "PROD";

const getEndPoint = ({stage, api}: {stage: Stage; api: "assetManagement" | "noise"}) => {
	const endPoints: Record<Stage, {assetManagement: string; noise: string}> = {
		INT: {
			assetManagement: `https://api-s.trumpf.com/asset-management/1.0/core/assets`,
			noise: "https://api.noise.dev.messaging.iot.trumpf.com/api",
		},
		QA: {
			assetManagement: `https://api-s.trumpf.com/asset-management/1.0/core/assets`,
			noise: `https://api-s.trumpf.com/noise-qa/1.0`, // TODO get rid of -qa
		},
		PROD: {
			assetManagement: `https://api-p.trumpf.com/asset-management/1.0/core/assets`,
			noise: `https://api-p.trumpf.com/noise/1.0`, //TODO endpoint not available yet
		},
	};

	return endPoints[stage][api];
};

const fetchOptions = async ({method}: {method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"}): Promise<RequestInit> => {
	const authToken = await getAuthToken();

	return {
		method,
		headers: new Headers({
			"Content-Type": "application/json",
			Authorization: `Bearer ${authToken}`,
		}),
	};
};

// ################################################## Notifications #################################################

export type RawNotification = {
	id: string;
	subject: string;
	message: string;
	severity: "error" | "warning" | "information";
	timestamp: number; // unix epoch time in seconds
	ttl: number; // in seconds
	sender: string; // translated category
};

export const getNotifications = async ({
	stage,
	locale,
	continuationToken,
	signal,
}: {
	stage: Stage;
	locale: string;
	continuationToken: string | null;
	signal: AbortSignal;
}) => {
	const options: RequestInit = {...(await fetchOptions({method: "GET"})), signal};

	const url = new URL(`${getEndPoint({stage, api: "noise"})}/notifications`);
	url.searchParams.append("locale", locale);
	if (continuationToken) url.searchParams.append("continuationToken", continuationToken);

	const response = await fetch(url, options);
	if (!response.ok) throw new Error(`Failed to fetch notifications: ${response.statusText}`);

	const data = (await response.json()) as {continuationToken: string | null; notifications: RawNotification[]};
	return data;
};

// ################################################## UserSettings ##################################################

export type Category = {
	id: string;
	name: string;
	title: string;
	businessCritical: boolean;
	tooltip: string;
	channels: {
		id: string;
		name: string;
		enabled: boolean;
	}[];
	severity: {
		information: boolean;
		warning: boolean;
		error: boolean;
	};
};

export type CategoryPatch = Pick<Category, "id"> & {
	channels?: Omit<Category["channels"][number], "name">[];
	severity?: Partial<Category["severity"]>;
};

export type RawAsset = {
	assetId: string;
	assetName: string;
};

export type UserAsset = {
	id: string;
	enabled: boolean;
};

export type Asset = {
	id: RawAsset["assetId"];
	name: RawAsset["assetName"];
	enabled: UserAsset["enabled"] | null;
};

export type AssetPatch = UserAsset;

export const getCategories = async ({stage, locale}: {stage: Stage; locale: string}) => {
	const options = await fetchOptions({method: "GET"});
	const url = new URL(`${getEndPoint({stage, api: "noise"})}/user-settings/categories`);
	url.searchParams.append("locale", locale);

	const response = await fetch(url, options);
	if (!response.ok) throw new Error(`Failed to fetch categories: ${response.statusText}`);

	const data = (await response.json()) as Category[];
	return data;
};

export const patchCategories = async ({stage, categoriesDiff}: {stage: Stage; categoriesDiff: CategoryPatch[]}) => {
	const options = await fetchOptions({method: "PATCH"});
	const url = new URL(`${getEndPoint({stage, api: "noise"})}/user-settings/categories`);

	const response = await fetch(url, {...options, body: JSON.stringify(categoriesDiff)});
	if (!response.ok) throw new Error(`Failed to patch categories: ${response.statusText}`);
};

export const getRawAssets = async ({stage}: {stage: Stage}) => {
	const rawAssets: RawAsset[] = [];
	let totalCount = Infinity;
	let offset = 0;

	const options = await fetchOptions({method: "GET"});
	const baseUrl = new URL(getEndPoint({stage, api: "assetManagement"}));
	baseUrl.searchParams.append("isRootAsset", "true");
	baseUrl.searchParams.append("limit", "100"); // max provided by api

	while (rawAssets.length < totalCount) {
		const tmpUrl = new URL(baseUrl);
		tmpUrl.searchParams.append("offset", offset.toString());

		const response = await fetch(tmpUrl, options);
		if (!response.ok) throw new Error(`Failed to fetch rawAssets: ${response.statusText}`);

		const data = (await response.json()) as {totalCount: number; results: (RawAsset & Record<string, unknown>)[]};
		rawAssets.push(...data.results);

		if (totalCount === Infinity) totalCount = data.totalCount; // update totalCount on first fetch
		offset += data.results.length;
	}

	return rawAssets;
};

export const getUserAssets = async ({stage}: {stage: Stage}) => {
	const options = await fetchOptions({method: "GET"});
	const url = new URL(`${getEndPoint({stage, api: "noise"})}/user-settings/assets`);

	const response = await fetch(url, options);
	if (!response.ok) throw new Error(`Failed to fetch userAssets: ${response.statusText}`);

	const data = (await response.json()) as UserAsset[];
	return data;
};

export const patchUserAssets = async ({stage, assetsDiff}: {stage: Stage; assetsDiff: AssetPatch[]}) => {
	const options = await fetchOptions({method: "PATCH"});
	const url = new URL(`${getEndPoint({stage, api: "noise"})}/user-settings/assets`);

	const response = await fetch(url, {...options, body: JSON.stringify(assetsDiff)});
	if (!response.ok) throw new Error(`Failed to patch assets: ${response.statusText}`);
};
